/**
 * 미러 신선도 배너 — "지금 보는 숫자가 얼마나 믿을 만한가"를 항상 눈에 보이게 한다.
 *
 * 표시 규칙:
 *   - reason 이 있으면(비연동 매장 / service=mogoo) 그 사유를 그대로 보여준다(상태 매핑보다 우선).
 *   - 아니면 freshnessStatus(fresh/stale/showing_last_success/never_synced)를 문구로 매핑한다.
 *   - 마지막 성공 동기화 시각 + 조회 기준(기간)을 함께 밝혀, 0/빈 값이 "실패"인지 "실제 없음"인지 구분한다.
 *
 * 이 화면은 읽기 전용 미러라는 사실을 배지로 못박는다: "ModernWash V1 읽기 전용 미러".
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import { STATUS_COLORS } from '../../styles/uiTokens';
import { formatDateTime } from '../../owner-console-v2/modules/carwash/modernwash/format';
import { freshnessMeta } from './mirrorFormat';

const MIRROR_BADGE = 'ModernWash V1 읽기 전용 미러';

const latestRunLabel = (status) => {
  if (status === 'succeeded') return '성공';
  if (status === 'partial') return '일부 실패';
  if (status === 'failed') return '실패';
  if (status === 'running') return '진행 중';
  return '실행 이력 없음';
};

const FreshnessBanner = ({ freshness, reason, basisLabel, sx }) => {
  const status = freshness?.freshnessStatus ?? 'never_synced';
  const meta = freshnessMeta(status);

  // reason(비연동/네이티브)이 있으면 상태 매핑보다 우선한다.
  const showReason = Boolean(reason);
  const tone = showReason ? 'neutral' : meta.tone;
  const c = STATUS_COLORS[tone] || STATUS_COLORS.neutral;
  const title = showReason ? '미러 데이터 없음' : meta.label;
  const desc = showReason ? reason : meta.desc;

  const lastAt = freshness?.lastSuccessfulAt;
  const lastText = lastAt ? formatDateTime(lastAt) : '없음 (성공 이력 없음)';
  const runText = latestRunLabel(freshness?.latestRunStatus);

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: { xs: 1, md: 2 },
        p: { xs: 1.5, md: 2 },
        borderRadius: '12px',
        border: `1px solid ${c.border}`,
        backgroundColor: c.bg,
        color: c.fg,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <CloudDoneIcon sx={{ fontSize: 20, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 800 }}>
              {title}
            </Typography>
            <Box
              component="span"
              sx={{
                px: 0.85,
                py: 0.25,
                borderRadius: '999px',
                fontSize: 10.5,
                fontWeight: 800,
                backgroundColor: 'rgba(15,23,42,0.08)',
                color: 'inherit',
                whiteSpace: 'nowrap',
              }}
            >
              {MIRROR_BADGE}
            </Box>
          </Box>
          <Typography sx={{ fontSize: 12, opacity: 0.9, mt: 0.25 }}>{desc}</Typography>
        </Box>
      </Box>

      <Box
        sx={{
          ml: { md: 'auto' },
          display: 'flex',
          flexDirection: { xs: 'row', md: 'column' },
          flexWrap: 'wrap',
          gap: { xs: 1.5, md: 0.25 },
          textAlign: { md: 'right' },
        }}
      >
        <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>
          마지막 성공 동기화: {lastText}
        </Typography>
        <Typography sx={{ fontSize: 11.5, opacity: 0.85 }}>
          최근 실행 결과: {runText}
        </Typography>
        {basisLabel ? (
          <Typography sx={{ fontSize: 11.5, opacity: 0.85 }}>데이터 기준: {basisLabel}</Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default FreshnessBanner;
